import { FC } from "react";
import { useFetcher, useNavigate, useParams } from "react-router";
import { Text } from "@postenbring/hedwig-react";
import Translated from "../infrastructure/translation/Translated";
import { CenteredSpinner } from "../CenteredSpinner/CenteredSpinner";
import { ClientForm } from "./ClientForm";
import styles from "./EditClientPage.module.css";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import {
  useAvailableAccounts,
  useClient,
  useClientCertificateOptions,
} from "./clientsRouteContext";
import { useClientForm } from "./useClientForm";
import { useClientPaths } from "./useClientPaths";
import { OAuthClient } from "../infrastructure/api/api";

export const EditClientPage: FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const translate = useTranslate();

  const client = useClient(clientId);

  if (!client) {
    return (
      <div className={styles.container}>
        <p className={styles.error}>{translate("client.client_not_found")}</p>
      </div>
    );
  }

  return <EditClientForm key={client.client.clientId} client={client.client} />;
};

const EditClientForm: FC<{ client: OAuthClient }> = ({ client }) => {
  const navigate = useNavigate();
  const paths = useClientPaths();

  const fetcher = useFetcher();
  const availableAccounts = useAvailableAccounts();
  const { certificates } = useClientCertificateOptions();
  const selectableIds = certificates.map((c) => c.certificate.id);

  const form = useClientForm({
    client,
    selectableCertificateIds: selectableIds,
    missingEndpointTextKey: "edit_client.error_no_endpoint",
    fetcher,
  });

  if (form.submitting) {
    return (
      <div className={styles.container}>
        <CenteredSpinner />
      </div>
    );
  }

  const unusableCertificateCount = client.certificateIds.filter(
    (id) => !selectableIds.includes(id),
  ).length;

  return (
    <div className={styles.container}>
      <Text variant="h1" as="h1" size="min" className={styles.title}>
        <Translated textKey={"edit_client.header"} />
      </Text>
      <Text variant="body-small" className={styles.intro}>
        <Translated textKey={"new_client.description"} />
      </Text>

      <ClientForm
        form={form}
        availableAccounts={availableAccounts}
        submitTextKey="edit_client.submit"
        onCancel={() => navigate(paths.client(client.clientId))}
        certificates={certificates}
        unusableCertificateCount={unusableCertificateCount}
      />
    </div>
  );
};
